import {
  Appointments,
  AppointmentStatus,
  AppointmentType,
  BusinessPlanType,
  Customers,
  Employees,
  EmployeesToBusinesses,
  InactiveEmployeesToBusinesses,
  Payee,
  PaymentMethod,
  PaymentStatus,
  PayrollType,
  PiercingLocations,
  ReferralSources,
  Roles,
  Users,
  WaiverRequests,
} from '@prisma/client';
import React, { SetStateAction } from 'react';
import { FormikHelpers, FormikValues } from 'formik';
import { FullPayment, PaymentsGroup } from './types/payments';
import { OrderWithPayments } from './types/orders';

export interface Appointment {
  appointmentId: string;
  employeeId: string;
  customerId: string;
  businessId?: string;
  status: AppointmentStatus;
  previousStatus?: AppointmentStatus;
  onschedId?: string;
  tattooLocation?: string;
  tattooDetails?: string;
  tattooSize?: string;
  tattooColor?: string;
  tattooInspiration?: string;
  prefTattooStyle?: string;
  isFirstTattoo?: boolean;
  isNewCustomer?: boolean;
  refImgsId?: string[];
  areaImgsId?: string[];
  budget?: number;
  skinTone?: string;
  otherNotes?: string;
  rejectionMessage?: string;
  priceType?: string;
  minPrice?: number;
  maxPrice?: number;
  hourlyPrice?: number;
  price?: number;
  appointmentLengthInMinutes?: number;
  depositPrice?: number;
  depositPaidOn?: string;
  rescheduledByCust?: number;
  customDesignMessage?: string;
  createdAt: Date;
  updatedAt?: Date;
  deletedAt?: Date;
  startDateTime?: string;
  endDateTime?: string;
  tripCity?: string;
  timezone?: string;
  employee: User;
  customer: User;
  depositCheckoutSessionActive: boolean;
  checkoutSessionActive: boolean;
  checkoutSessionCompleted: boolean;
  checkoutCompletedOn?: string;
  tipAmt: number;
  appointmentType: AppointmentType;
  serviceCategory?: string;
  acceptedOn?: Date;
  rejectedOn?: Date;
  canceledOn?: Date;
  confirmedOn?: string;
  paymentStatus?: string;
  serviceId?: number;
  piercingLocationId?: number;
  paymentMethod?: PaymentMethod;
  depositPaymentMethod?: PaymentMethod;
  reschedulingFeesPaid?: number;
  reschedulingFeesRefundAmount?: number;
  bookerUserId?: string;
  canceledByUserId?: string;
  internalNotes?: string;
  wasNoShow?: boolean;
  includeEmployeeBonus?: boolean;
  businessTripId?: string;
  waiverRequest?: WaiverRequests | null;
  availability?: string;
  archivedAt?: Date;
  ongoingProject?: boolean;
  coverUp?: boolean;
  tattooServiceType?: string;
  surroundingTattoos?: boolean;
  isEphemeral?: boolean;
  isDepositRefunded?: boolean;
  referralSources?: ReferralSources[];
  isVirtual?: boolean;
  virtualMeetingUrl?: string;
  virtualMeetingProvider?: string;
}

export interface AppointmentWithCustomerAndEmployee extends Appointments {
  customer: Customers & Users;
  employee: Employees & Users;
}

export interface GeneralRequest {
  requestId: string;
  customerId: string;
  businessId: string;

  tattooLocation?: string;
  tattooDetails?: string;
  tattooSize?: string;
  tattooColor?: string;
  tattooInspiration?: string;
  prefTattooStyle?: string;
  isFirstTattoo?: boolean;
  isNewCustomer?: boolean;
  refImgsId?: string[];
  areaImgsId?: string[];
  budget?: number;
  skinTone?: string;
  otherNotes?: string;
  availability?: string;
  ongoingProject?: boolean;
  rejectionMessage?: string;
  coverUp?: boolean;
  tattooServiceType?: string;
  surroundingTattoos?: boolean;
  isEphemeral?: boolean;
  selectedArtists?: string[];

  createdAt: Date;
  updatedAt?: Date;
  deletedAt?: Date;

  customer: User;
  referralSources?: ReferralSources[];
}

export interface Row {
  rowId: string;
  cells: React.ReactNode[];
}

export interface TabInt {
  id?: string | number;
  label: string;
  component: React.ReactNode;
  hidden?: boolean;
}

export interface CalendarItem {
  start: Date;
  end: Date;
  customerName: string;
  employeeName: string;
  employeeImg?: string;
  tattooLocation: string | undefined;
  appointmentId: string;
  button: React.ReactNode;
  editButton: React.ReactNode;
}

export interface CalendarSection {
  day: Date;
  calendarItems: CalendarItem[];
}

export interface Option<
  TValue extends string | number | boolean = string | number | boolean,
> {
  value: TValue;
  label?: string;
}

export interface Input {
  name: string;
  label: string;
  type:
    | 'text'
    | 'number'
    | 'dollar'
    | 'select'
    | 'multiselect'
    | 'textarea'
    | 'image'
    | 'phone'
    | 'scheduler'
    | 'link'
    | 'percent'
    | 'date'
    | 'date-range'
    | 'time'
    | 'color';
  options?: Option[];
  placeholder?: string;
  disabled?: any;
  hidden?: any;
  prependText?: any;
  helperText?: string;
  // need to add support in specific Input component
  tooltip?: string;
  validateChange?: (value: string) => boolean | Promise<boolean>;
  allText?: string;
  autoWidth?: boolean;
  showListWithAll?: boolean;
  hideAllOption?: boolean;
  minDate?: Date;
  displayEmpty?: boolean;
  minutesStep?: number;
  min?: number;
  max?: number;
  step?: number;
  colors?: string[];
}

export type InputRowType = Input[];

export interface InputGroupType {
  name: string;
  inputRows: InputRowType[];
}

export type FileSectionRow = {
  type: 'FILE';
  value: {
    id: string;
    url: string;
    name: string;
    type: 'image' | 'pdf';
    showName?: boolean;
    actionComponent?: React.ReactNode;
  }[];
};

export type ColorSectionRow = {
  type: 'COLOR';
  value: string[];
};

export type SectionRow = {
  id?: string;
  label: string;
  action?: () => void;
} & (
  | {
      type: 'IMAGE' | 'TEXT' | 'PHONE' | 'LINK';
      value: string[] | (string[] | undefined)[];
    }
  | {
      type: 'NODE';
      value: React.ReactNode[];
    }
  | FileSectionRow
  | ColorSectionRow
);

export interface Section {
  name: string;
  rows: SectionRow[];
  values?: any;
  buttonText?: string | React.ReactNode;
  buttonAction?: () => void;
  noGap?: boolean;
}

export interface ModalData {
  name: string;
  sections: Section[];
}

export interface FooterAction {
  label: string;
  action: any;
}

export interface Employee {
  employeeId: string;
  profileImage: string;
  urlPath: string;
  cancellationPolicyText: string;
  liabilityWaiver: string;
  stackDays: boolean;
  stackTimes: boolean;
  createdAt: string;
  updatedAt: string;
  calendarAuthorized: boolean;
  standardDesignMessage?: string;
  onschedResourceId?: string;
  minAppointmentPrice?: number;
  maxCancellationTimes?: number;
  minCancellationDays?: number;
  bookAheadLimitDays?: number;
  displayName?: string;
  deletedAt?: string;
  calendarEmail?: string;
  calendarAuthUrl?: string;
  about?: string;
  user?: User;
  paymentCustomerId?: string;
  employeesToBusinesses?: [
    {
      employeeId: string;
      businessId: string;
      canViewReports: boolean;
      canManageTeam: boolean;
      canManageWaivers: boolean;
      canIssueRefunds: boolean;
      canManageCampaigns: boolean;
      canViewAllShopCustomers: boolean;
      generalRequestOwner: boolean;
      payrollType: PayrollType;
      paymentToEmployeePercent: number;
      selectedRoles: EmployeesToBusinesses['selectedRoles'];
      isBookable?: boolean;
    },
  ];
}

export type Customer = Customers & {
  user: Users;
  appointments: Appointments[];
  internalNotes: string | null;
};

export interface Service {
  defaultDurationInMinutes: number;
  defaultPrice: number;
  defaultDepositPrice: number | null;
  enableDirectBookings: boolean;
  minimumAgeInMonths: number;
  serviceCategory: string;
  serviceDescription: string;
  serviceName: string;
  serviceId: number;
  employeesToServices: {
    employeeId: string;
    serviceId: number;
  }[];
  piercingLocations?: PiercingLocations[];
}

export interface EmployeeToBusiness {
  employeeId: string;
  profileImage: string;
  urlPath: string;
  cancellationPolicyText: string;
  liabilityWaiver: string;
  stackDays: boolean;
  stackTimes: boolean;
  createdAt: string;
  updatedAt: string;
  calendarAuthorized: boolean;
  standardDesignMessage?: string;
  onschedResourceId?: string;
  maxCancellationTimes?: number;
  minCancellationDays?: number;
  bookAheadLimitDays?: number;
  displayName?: string;
  deletedAt?: string;
  calendarEmail?: string;
  calendarAuthUrl?: string;
  about?: string;
  user?: User;
  payrollType: PayrollType;
  depositToEmployeePercent: number;
  tipToEmployeePercent: number;
  paymentToEmployeePercent: number;
  isBookable: boolean;
  bonusCommissionPercent?: number;
  addToAllChats: boolean;
  generalRequestOwner: boolean;
  canViewReports: boolean;
  canManageWaivers: boolean;
  canIssueRefunds: boolean;
  canManageCampaigns: boolean;
  payeeForOtherPayroll: Payee;
  canManageTeam: boolean;
  canViewAllShopCustomers: boolean;
  selectedRoles: EmployeesToBusinesses['selectedRoles'];
  color: string | null;
  sortOrder?: number;
}

export interface Business {
  businessId: string;
  name: string;
  urlPath: string;
  coverImage: string;
  thumbnailImage: string;
  city: string;
  state: string;
  zip: string;
  streetAddress: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  streetAddress2?: string;
  services?: Service[];
  defaultTattooDeposit?: number;
  terminalReaderId?: string;
  disableCreditTipping: boolean;
  employeesToBusinesses?: [
    {
      payeeForOtherPayroll: Payee;
      businessId: string;
      employeeId: string;
      employee: Employee;
      payrollType: PayrollType;
      depositToEmployeePercent: number;
      tipToEmployeePercent: number;
      paymentToEmployeePercent: number;
      bonusCommissionPercent?: number;
      isBookable: boolean;
      addToAllChats: boolean;
      canViewReports: boolean;
      canManageWaivers: boolean;
      canIssueRefunds: boolean;
      canManageCampaigns: boolean;
      generalRequestOwner: boolean;
      canManageTeam: boolean;
      canViewAllShopCustomers: boolean;
      selectedRoles: EmployeesToBusinesses['selectedRoles'];
      color: string | null;
      sortOrder?: number;
    },
  ];
  inactiveEmployeesToBusinesses: Pick<
    InactiveEmployeesToBusinesses,
    'businessId'
  >[];
  inStoreCustomerServiceFeeRate?: number;
  onlineCustomerServiceFeeRate?: number;
  businessTaxRates?: {
    serviceTaxRate: number;
    productTaxRate: number;
  };
  type: BusinessPlanType;
  tattooAftercareLink: string | null;
  reviewLink: string | null;
  allowGeneralRequests: boolean;
  stationMax: number | null;
  businessTrips: Trip[];
  isTerminalPurchased: boolean;
}

export interface User {
  dob?: string;
  authServiceId: string;
  userId: string;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: Roles;
  isActive: boolean;
  timezone: string;
  updatedAt: string;
  employee: Employee;
  igHandle?: string;
  deletedAt?: string;
  displayName?: string;
  internalNotes?: string;
}

export interface LoadedUser {
  authServiceId: string;
  userId: string;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: Roles;
  isActive: boolean;
  timezone: string;
  updatedAt: string;
  employee: Employee;
  igHandle?: string;
  deletedAt?: string;
  canViewReports: boolean;
  canManageTeam: boolean;
  canManageWaivers: boolean;
  canIssueRefunds: boolean;
  canManageCampaigns: boolean;
  canViewAllShopCustomers: boolean;
  generalRequestOwner: boolean;
  business: {
    id: string;
    name: string;
    urlPath: string;
    coverImage: string;
    thumbnailImage: string;
    city: string;
    state: string;
    zip: string;
    streetAddress: string;
    streetAddress2?: string;
    employees: EmployeeToBusiness[];
    services?: Service[];
    defaultTattooDeposit?: number;
    terminalReaderId?: string;
    inStoreCustomerServiceFeeRate?: number;
    onlineCustomerServiceFeeRate?: number;
    businessTaxRates?: {
      serviceTaxRate: number;
      productTaxRate: number;
    };
    type: BusinessPlanType;
    tattooAftercareLink: string | null;
    reviewLink: string | null;
    allowGeneralRequests: boolean;
    stationMax: number | null;
    businessTrips: Trip[];
    disableCreditTipping: boolean;
    inactiveEmployeesToBusinesses: Pick<
      InactiveEmployeesToBusinesses,
      'businessId'
    >[];
    isTerminalPurchased: boolean;
  };
  allBusinesses: Business[];
}

export interface Filter {
  name: string;
  options: FilterOption[];
  state: string[];
  setState: React.Dispatch<SetStateAction<string[]>>;
  emptyLabel?: string;
  allLabel?: string;
}

export interface FilterOption {
  label: string;
  value: string;
  color?: string;
}

export interface AvailableTimes {
  allowableBookings: number;
  allowableCapacity: number;
  availableBookings: number;
  availableCapacity: number;
  date: string;
  displayTime: string;
  duration: number;
  endDateTime: string;
  resourceId: string;
  startDateTime: string;
  time: number;
  travelAppointmentId: string;
  travelTimeMins: number;
}

export interface DayItem {
  available: boolean;
  date: string;
  preferred?: boolean;
}

export interface Trip {
  tripId: string;
  employeeId?: string;
  businessId?: string;
  city: string;
  state: string;
  zip: string;
  timezone: string;
  startDateTime: string;
  endDateTime: string;
  booksOpen: Boolean;
  studioName?: string;
  streetAddress?: string;
  streetAddress2?: string;
}

// Type for total sales by date
export interface SalesData {
  date: string;
  sales: number;
}

// Type for total appointments by date
export interface AppointmentsData {
  date: string;
  appointments: number;
}

// Type for total sales by employee
export interface SalesByEmployee {
  artist: string;
  sales: number;
  color?: string;
}

// Type for count of appointments by appointment type
export interface AppointmentsByType {
  appointmentType: string;
  appointments: number;
}

export interface PiercingLocation {
  locationId: number;
  locationCategory: string;
  locationType: string;
}

export interface Transaction {
  id: string;
  amount: number;
  tipAmount?: number;
  dateCreated: Date;
  dateUpdated: Date;
  type: string;
  discountAmount?: number;
  chargeReason?: string;
  status?: PaymentStatus;
  error?: string;
  payment?: FullPayment;
  appointment?: Appointment;
  order?: OrderWithPayments;
  paymentsGroup?: PaymentsGroup;
  customer?: Customer;
}

export interface CheckoutFailureData {
  failureCode: string;
  failureMessage: string;
  status: string;
  type: string;
  paymentIntentId: string;
}

export interface ModalState {
  isOpen: boolean;
  status: 'loading' | 'idle' | 'failed' | 'success';
  title: string;
  message: string;
  buttonText: string;
}

export interface TerminalCheckoutModalState extends ModalState {}

export enum PayoutDistributionOptions {
  COMMISSION = 'COMMISSION',
  BUSINESS = 'BUSINESS',
  EMPLOYEE = 'EMPLOYEE',
  EMPTY = '',
}

export interface FormikForm<Values extends FormikValues = any> {
  validationSchema?: any | (() => any);
  initialValues: Values;
  onSubmit: (
    values: Values,
    formikHelpers: FormikHelpers<Values>,
  ) => void | Promise<any>;
}
